import { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import UaParser from 'ua-parser-js';
import { deviceType, isAndroid, isDesktop, isIOS, isIOS13, isIPhone13, isMobile, isTablet } from 'react-device-detect';

import { AppState, useAppDispatch, useAppSelector } from 'store';
import { DeviceState, initialState, setDevice } from 'store/slices/device';
import { useBrowserLayoutEffect } from 'hooks/useBrowserLayoutEffect';

// =================================================================================
type InitialState = { initialized: boolean } & any;

// =================================================================================

interface ContextProps {
    state: InitialState;
}

const AppContext = createContext<ContextProps>({
    state: initialState
});

// =======================================================
type AppProviderProps = { children: ReactNode };
// =======================================================

export const AppProvider: FC<AppProviderProps> = ({ children }) => {
    const dispatch = useAppDispatch();

    const state: DeviceState = useAppSelector((state: AppState) => state);

    useBrowserLayoutEffect(() => {
        const result = UaParser(window.navigator.userAgent);

        const browserInfo = {
            ...result,
            deviceType,
            isIOS: isIOS || isIOS13 || isIPhone13,
            isAndroid,
            isMobile,
            isTablet,
            isDesktop
        };

        dispatch(setDevice({ initialized: true, ...browserInfo }));
    }, []);

    const contextValue = useMemo(() => ({ state }), [state]);
    return <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>;
};

export const useAppContext = () => useContext<ContextProps>(AppContext);

export default AppContext;
